import * as React from 'react';
import { useEffect } from 'react';
import { lowerCase } from 'lodash';
import { loadStripe, Stripe, StripeCardElement, StripeCardElementChangeEvent } from '@stripe/stripe-js';
// # exceptions
import { MissingDependencyException } from '@/exceptions';
// # Interfaces
import { iStripeCard, iPaymentCard } from '@/interfaces/payments';
import { iStripeService } from '@/interfaces/services/stripe';
// # Components
import { Button } from '@/components/content';
import { ValidationErrorsList } from '@/components/validation';
// # Hooks
import { useForm } from '@/hooks';
// @/components/payment/PaymentCardForm.tsx
import { makeStripeService } from '@/interfaces/services/stripe';

import {Elements} from '@stripe/react-stripe-js';
import SetupForm from './SetupForm';

interface iProps {
    /**
     * Optionally provide some initial card details.
     */
    card?: Partial<iStripeCard>;
    /**
     * Function called when the form is submitted.
     * If a promise is returned, the component will remain in a loading state until resolved.
     */
    onSubmit: (card: iPaymentCard) => any | Promise<any>;
    /**
     * The stripe service used for generating card tokens.
     */
    stripeService?: iStripeService;
}

export const PaymentCardForm: React.SFC<iProps> = (props: iProps) => {
    const stripeService = props.stripeService || makeStripeService();
    const { validateForm } = useForm<iStripeCard>();

    if (!stripeService) {
        throw new MissingDependencyException('stripeService', '<PaymentCardForm>');
    }

    const defaultCard: iStripeCard = {
        cvc: '',
        exp_month: null,
        exp_year: null,
        number: '',
    };

    // Hooks.
    const { data: card, isValid, updateFields: setCard, validationErrors } = useForm<iStripeCard>({
        initialValues: {
            ...defaultCard,
            ...props.card,
        },
    });

    const [showValidation, setShowValidation] = React.useState(false);
    const [isBusy, setBusy] = React.useState(false);


    const [publishableKey, setPublishableKey] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchPublishableKey = async () => {
            try {
                const response = await fetch('/api/stripe');
                const data = await response.json();

                if (response.ok) {
                    setPublishableKey(data.stripeKey);
                } else {
                    console.error('Failed to fetch publishable key:', data.error);
                }
            } catch (error) {
                console.error('Error fetching publishable key:', error);
            }
        };

        fetchPublishableKey();
    }, []);

    if (!publishableKey) {
        // You can render a loading state or handle it as needed
        return <div>Loading...</div>;
    }

    const stripePromise = loadStripe(publishableKey);

    const options = {
        // passing the SetupIntent's client secret
        clientSecret: card.setup_intent,
        // Fully customizable with appearance API.
        appearance: {/*...*/},
    };

    const handleCardDetailsReceived = (cardDetails) => {
        // Do something with the received card details in the parent component
        setBusy(false);
        // eslint-disable-next-line no-param-reassign
        props.onSubmit({
            brand: lowerCase(cardDetails.card_brand ?? ''),
            last_four: cardDetails.last_four ?? '',
            name: '',
            stripe_id: cardDetails.stripe_id,
        });
    };


    return (
        <Elements stripe={stripePromise} options={options}>
          <SetupForm card={card} onCardDetailsReceived={handleCardDetailsReceived}/>
        </Elements>
    );
};
PaymentCardForm.displayName = 'PaymentCardForm';
PaymentCardForm.defaultProps = {};
